import { useState, useEffect, useRef } from "react";
import useIsMobile from "./useIsMobile";

const STORIES = [
  {
    id: "nz-flood-response-2026",
    slug: "nz-flood-response-2026",
    headline: "West Coast flood response draws criticism",
    origin: "editor",
    status: "in-progress",
    priority: "urgent",
    lastUpdated: "2m ago",
    sources: 4,
    artefacts: ["article", "video-short", "tv-package"],
    published: ["article"],
    beat: "National",
  },
  {
    id: "housing-affordability-data",
    slug: "housing-affordability-data",
    headline: "Housing affordability reaches historic low",
    origin: "ai-data",
    status: "ready",
    priority: "high",
    lastUpdated: "12m ago",
    sources: 7,
    artefacts: ["article", "audio", "chatbot"],
    published: [],
    beat: "Economy",
  },
  {
    id: "all-blacks-coach-speculation",
    slug: "all-blacks-coach-speculation",
    headline: "All Blacks coaching future uncertain",
    origin: "trending",
    status: "awaiting-input",
    priority: "medium",
    lastUpdated: "34m ago",
    sources: 2,
    artefacts: ["article"],
    published: [],
    beat: "Sport",
  },
  {
    id: "auckland-transport-levy",
    slug: "auckland-transport-levy",
    headline: "Auckland transport levy proposal leaked",
    origin: "editor",
    status: "awaiting-input",
    priority: "high",
    lastUpdated: "1h ago",
    sources: 1,
    artefacts: [],
    published: [],
    beat: "Auckland",
  },
  {
    id: "supermarket-duopoly-report",
    slug: "supermarket-duopoly-report",
    headline: "Commerce Commission flags supermarket pricing",
    origin: "ai-data",
    status: "in-progress",
    priority: "medium",
    lastUpdated: "2h ago",
    sources: 9,
    artefacts: ["article", "audio", "video-short", "tv-package", "chatbot"],
    published: ["article", "audio"],
    beat: "Economy",
  },
  {
    id: "parliament-no-confidence",
    slug: "parliament-no-confidence",
    headline: "No confidence motion tabled in Parliament",
    origin: "trending",
    status: "ready",
    priority: "urgent",
    lastUpdated: "5m ago",
    sources: 5,
    artefacts: ["article", "tv-package"],
    published: ["article"],
    beat: "Politics",
  },
];

const TIMELINE = [
  { time: "09:14", type: "submit", actor: "Field Reporter", content: "Voice note: 3 residents confirmed displaced, Red Cross on site" },
  { time: "09:22", type: "ai", actor: "AI", content: "Article updated — new quotes integrated, lede revised" },
  { time: "09:35", type: "submit", actor: "KL", content: "Added council statement PDF" },
  { time: "09:41", type: "ai", actor: "AI", content: "TV package script regenerated — council response added to reporter track" },
  { time: "10:02", type: "submit", actor: "BE", content: "Satellite imagery sourced — 3 images added" },
  { time: "10:08", type: "ai", actor: "AI", content: "Image captions generated, article photo selection updated" },
  { time: "10:15", type: "flag", actor: "AI", content: "Competitor coverage detected — Newshub reporting 47 displaced (we have 3)" },
];

const RELATED = [
  { pub: "RNZ", headline: "West Coast Civil Defence declares state of emergency", delta: "Has official declaration — we don't" },
  { pub: "Newshub", headline: "47 families displaced in overnight flooding", delta: "Higher displacement figure — needs verification" },
  { pub: "NZ Herald", headline: "Insurance claims to exceed $40m", delta: "Insurance estimate — not in our coverage" },
];

const SUGGESTIONS = [
  "Follow up with Civil Defence on official declaration status",
  "Verify displacement figures — 3 vs 47 discrepancy needs resolution",
  "Commission long-form investigation: infrastructure underinvestment pattern",
  "Interview insurance broker on claims outlook",
];

const COMMISSIONS = [
  { reporter: "Sarah M.", task: "Get official Civil Defence numbers", due: "2pm today", status: "accepted" },
  { reporter: "James T.", task: "Photograph road damage on SH6", due: "4pm today", status: "pending" },
];

const HEADLINES = [
  "West Coast floods: 'We had minutes to get out,' says resident",
  "Flood chaos exposes years of infrastructure neglect, experts say",
  "Hundreds flee as West Coast rivers break banks",
];

const ARTEFACT_META = {
  article: { label: "Article", icon: "📄", color: "#3b82f6" },
  audio: { label: "Audio Story", icon: "🎙️", color: "#8b5cf6" },
  "video-short": { label: "Short Video", icon: "📱", color: "#ec4899" },
  "tv-package": { label: "TV Package", icon: "📺", color: "#f59e0b" },
  chatbot: { label: "AI Chatbot", icon: "💬", color: "#10b981" },
};

const originLabels = {
  editor: { label: "Editor", color: "#3b82f6", bg: "#1e3a5f" },
  "ai-data": { label: "AI Signal", color: "#10b981", bg: "#0d3326" },
  trending: { label: "Trending", color: "#f59e0b", bg: "#3d2800" },
};

const statusLabels = {
  "awaiting-input": { label: "Awaiting Input", dot: "#f59e0b" },
  "in-progress": { label: "In Progress", dot: "#3b82f6" },
  ready: { label: "Ready", dot: "#10b981" },
};

const priorityColors = { urgent: "#ef4444", high: "#f59e0b", medium: "#6b7280" };

function PulsingDot({ color = "#3b82f6", size = 8 }) {
  return (
    <span style={{ position: "relative", display: "inline-block", width: size, height: size }}>
      <span style={{
        position: "absolute", inset: 0, borderRadius: "50%", background: color,
        animation: "ping 1.5s cubic-bezier(0,0,0.2,1) infinite", opacity: 0.4
      }} />
      <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: color }} />
    </span>
  );
}

function StoryCard({ story, selected, onClick, animating }) {
  const origin = originLabels[story.origin];
  const status = statusLabels[story.status];
  return (
    <div onClick={onClick} style={{
      padding: "12px 14px",
      background: selected ? "#1a2332" : animating ? "#161f2e" : "#0f1623",
      border: `1px solid ${selected ? "#3b82f6" : animating ? "#3b82f650" : "#1e2d42"}`,
      borderRadius: 8,
      cursor: "pointer",
      transition: "all 0.15s",
      position: "relative",
      overflow: "hidden",
    }}>
      {animating && (
        <div style={{
          position: "absolute", inset: 0, background: "linear-gradient(90deg, transparent, #3b82f610, transparent)",
          animation: "shimmer 1.5s infinite",
        }} />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", padding: "2px 6px",
          borderRadius: 3, color: origin.color, background: origin.bg, textTransform: "uppercase"
        }}>{origin.label}</span>
        <span style={{ fontSize: 9, color: priorityColors[story.priority], fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {story.priority}
        </span>
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4 }}>
          {animating && <PulsingDot color="#3b82f6" size={6} />}
          <PulsingDot color={status.dot} size={6} />
        </span>
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, marginBottom: 6, fontFamily: "'IBM Plex Sans', sans-serif" }}>
        {story.headline}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ fontSize: 10, color: "#475569" }}>{story.beat}</span>
        <span style={{ fontSize: 10, color: "#334155" }}>·</span>
        <span style={{ fontSize: 10, color: "#475569" }}>{story.lastUpdated}</span>
        <span style={{ marginLeft: "auto", fontSize: 10, color: "#64748b" }}>{story.sources} src</span>
      </div>
      {story.artefacts.length > 0 && (
        <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
          {story.artefacts.map(a => (
            <span key={a} style={{
              fontSize: 9, padding: "1px 5px", borderRadius: 3,
              background: story.published.includes(a) ? "#0d3326" : "#1e2d42",
              color: story.published.includes(a) ? "#10b981" : "#64748b",
              border: `1px solid ${story.published.includes(a) ? "#10b98140" : "#1e3a5f"}`,
            }}>{ARTEFACT_META[a]?.icon} {story.published.includes(a) ? "Live" : "Draft"}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineItem({ item }) {
  const colors = { submit: "#3b82f6", ai: "#10b981", flag: "#ef4444" };
  return (
    <div style={{ display: "flex", gap: 12, position: "relative" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: colors[item.type], marginTop: 4, flexShrink: 0 }} />
        <div style={{ width: 1, background: "#1e2d42", flex: 1, marginTop: 4 }} />
      </div>
      <div style={{ paddingBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 2 }}>
          <span style={{ fontSize: 10, color: "#475569" }}>{item.time}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: colors[item.type] }}>{item.actor}</span>
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.5 }}>{item.content}</div>
      </div>
    </div>
  );
}

function ArtefactCard({ type, isPublished, isAvailable }) {
  const meta = ARTEFACT_META[type];
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "#1a2332" : "#0f1623",
        border: `1px solid ${isPublished ? "#10b98140" : isAvailable ? "#1e3a5f" : "#0f1623"}`,
        borderRadius: 8, padding: "14px 16px", cursor: "pointer",
        transition: "all 0.15s", opacity: isAvailable ? 1 : 0.35,
      }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 20 }}>{meta.icon}</span>
        {isPublished && (
          <span style={{ fontSize: 9, fontWeight: 700, color: "#10b981", background: "#0d3326", padding: "2px 7px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.06em" }}>Live</span>
        )}
        {isAvailable && !isPublished && (
          <span style={{ fontSize: 9, fontWeight: 700, color: "#64748b", background: "#1e2d42", padding: "2px 7px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.06em" }}>Draft</span>
        )}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", marginBottom: 4 }}>{meta.label}</div>
      {isAvailable && (
        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          <button style={{ flex: 1, fontSize: 10, padding: "4px 0", background: "#1e3a5f", border: "none", borderRadius: 4, color: "#93c5fd", cursor: "pointer", fontWeight: 600 }}>Review</button>
          {!isPublished && <button style={{ flex: 1, fontSize: 10, padding: "4px 0", background: "#0d3326", border: "none", borderRadius: 4, color: "#6ee7b7", cursor: "pointer", fontWeight: 600 }}>Publish</button>}
        </div>
      )}
    </div>
  );
}

function UploadZone() {
  const [dragging, setDragging] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={() => setDragging(false)}
      style={{
        border: `2px dashed ${dragging ? "#3b82f6" : "#1e3a5f"}`,
        borderRadius: 8, padding: "20px 16px", textAlign: "center",
        background: dragging ? "#1e3a5f20" : "transparent",
        transition: "all 0.15s", cursor: "pointer",
      }}>
      <div style={{ fontSize: 22, marginBottom: 6 }}>📎</div>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>Drop files or tap to add to story</div>
      <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
        {["📷 Image", "🎬 Video", "🎙️ Audio", "📄 Document", "📝 Notes"].map(t => (
          <span key={t} style={{ fontSize: 10, padding: "3px 8px", background: "#1e2d42", borderRadius: 4, color: "#94a3b8", cursor: "pointer" }}>{t}</span>
        ))}
      </div>
    </div>
  );
}

export default function NewsroomAI() {
  const [selected, setSelected] = useState(STORIES[0]);
  const [animating, setAnimating] = useState({ "housing-affordability-data": true });
  const [activeTab, setActiveTab] = useState("overview");
  const [filterOrigin, setFilterOrigin] = useState("all");
  const [mobShowDetail, setMobShowDetail] = useState(false);
  const [mobSlide, setMobSlide] = useState(null); // "in" or "out"
  const [loading, setLoading] = useState({}); // { buttonId: true }
  const mob = useIsMobile();

  function simulateAction(id, durationMs = 1500) {
    setLoading(prev => ({ ...prev, [id]: true }));
    setTimeout(() => setLoading(prev => ({ ...prev, [id]: false })), durationMs);
  }

  function showDetail(story) {
    setSelected(story);
    setMobSlide("in");
    setMobShowDetail(true);
    setTimeout(() => setMobSlide(null), 200);
  }
  function hideDetail() {
    setMobSlide("out");
    setTimeout(() => { setMobShowDetail(false); setMobSlide(null); }, 150);
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const randomStory = STORIES[Math.floor(Math.random() * STORIES.length)];
      setAnimating(prev => ({ ...prev, [randomStory.id]: true }));
      setTimeout(() => setAnimating(prev => ({ ...prev, [randomStory.id]: false })), 3000);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const filtered = filterOrigin === "all" ? STORIES : STORIES.filter(s => s.origin === filterOrigin);

  const allArtefactTypes = ["article", "audio", "video-short", "tv-package", "chatbot"];

  return (
    <div style={{
      fontFamily: "'IBM Plex Sans', 'Helvetica Neue', sans-serif",
      background: "#070d1a",
      color: "#e2e8f0",
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        @keyframes ping { 0% { transform: scale(1); opacity: 0.4; } 75%, 100% { transform: scale(2.2); opacity: 0; } }
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        @keyframes pulse-border { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
        @keyframes slideIn { from { transform: translateX(30%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes slideOut { from { transform: translateX(0); opacity: 1; } to { transform: translateX(30%); opacity: 0; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #1e3a5f; border-radius: 2px; }
        button:hover { opacity: 0.85; background-filter: brightness(1.15); }
        button:active { transform: scale(0.97); }
        button:focus-visible { outline: 2px solid #3b82f6; outline-offset: 2px; }
        input:focus-visible { outline: 2px solid #3b82f6; outline-offset: 1px; }
      `}</style>

      {/* Top bar */}
      <div style={{
        height: 48, borderBottom: "1px solid #1e2d42", display: "flex", alignItems: "center",
        padding: mob ? "0 12px" : "0 20px", gap: mob ? 8 : 16, flexShrink: 0, background: "#070d1a",
      }}>
        {mob && mobShowDetail && (
          <button onClick={hideDetail} style={{ fontSize: 11, color: "#93c5fd", background: "transparent", border: "none", cursor: "pointer", padding: "4px 8px", fontWeight: 600 }}>← Stories</button>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 20, height: 20, background: "#3b82f6", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 900, color: "#fff" }}>S</span>
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0", letterSpacing: "-0.01em" }}>Newsroom</span>
          {!mob && <span style={{ fontSize: 13, color: "#334155", fontWeight: 400 }}>/</span>}
          {!mob && <span style={{ fontSize: 13, color: "#64748b" }}>Newsroom AI</span>}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center" }}>
          {!mob && <span style={{ fontSize: 11, color: "#475569" }}>{new Date().toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })} NZST</span>}
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <PulsingDot color="#10b981" size={6} />
            <span style={{ fontSize: 11, color: "#10b981" }}>{mob ? "6" : "6 active stories"}</span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Left sidebar */}
        <div style={{ width: mob ? "100%" : 260, borderRight: mob ? "none" : "1px solid #1e2d42", display: mob && mobShowDetail ? "none" : "flex", flexDirection: "column", flexShrink: 0 }}>
          {/* Filter tabs */}
          <div style={{ padding: "12px 12px 8px", borderBottom: "1px solid #1e2d42" }}>
            <div style={{ display: "flex", gap: 4, background: "#0f1623", borderRadius: 6, padding: 3 }}>
              {[["all", "All"], ["editor", "Editor"], ["ai-data", "AI"], ["trending", "Trending"]].map(([val, label]) => (
                <button key={val} onClick={() => setFilterOrigin(val)} style={{
                  flex: 1, fontSize: 10, fontWeight: 600, padding: "8px 0", borderRadius: 4, border: "none",
                  background: filterOrigin === val ? "#1e3a5f" : "transparent",
                  color: filterOrigin === val ? "#93c5fd" : "#475569", cursor: "pointer", transition: "all 0.15s",
                }}>{label}</button>
              ))}
            </div>
          </div>

          {/* Story list */}
          <div style={{ flex: 1, overflow: "auto", padding: "10px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "24px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>No stories match this filter.</div>
                <button onClick={() => setFilterOrigin("all")} style={{
                  fontSize: 11, color: "#93c5fd", background: "transparent", border: "none",
                  cursor: "pointer", textDecoration: "underline", padding: 0,
                }}>Show all stories</button>
              </div>
            ) : (
              filtered.map(story => (
                <StoryCard
                  key={story.id}
                  story={story}
                  selected={selected?.id === story.id}
                  animating={!!animating[story.id]}
                  onClick={() => { setSelected(story); if (mob) showDetail(story); }}
                />
              ))
            )}
          </div>

          {/* Add story */}
          <div style={{ padding: "10px 10px", borderTop: "1px solid #1e2d42" }}>
            <button style={{
              width: "100%", padding: "8px", background: "#1e3a5f", border: "1px dashed #3b82f640",
              borderRadius: 6, color: "#93c5fd", fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}>+ New Story Slug</button>
          </div>
        </div>

        {/* Main panel */}
        {selected && (!mob || mobShowDetail) && (
          <div style={{
            flex: 1, display: "flex", flexDirection: "column", overflow: "hidden",
            ...(mob && mobSlide === "in" ? { animation: "slideIn 0.2s ease-out" } : {}),
            ...(mob && mobSlide === "out" ? { animation: "slideOut 0.15s ease-in" } : {}),
          }}>
            {/* Story header */}
            <div style={{ padding: mob ? "12px 14px" : "16px 24px", borderBottom: "1px solid #1e2d42", flexShrink: 0 }}>
              <div style={{ display: "flex", flexDirection: mob ? "column" : "row", alignItems: mob ? "stretch" : "flex-start", gap: 12, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", padding: "2px 7px",
                      borderRadius: 3, textTransform: "uppercase",
                      color: originLabels[selected.origin].color,
                      background: originLabels[selected.origin].bg,
                    }}>{originLabels[selected.origin].label}</span>
                    <span style={{ fontSize: 9, color: "#475569", fontFamily: "'IBM Plex Mono', monospace" }}>{selected.slug}</span>
                    <span style={{ fontSize: 9, color: priorityColors[selected.priority], fontWeight: 700, textTransform: "uppercase" }}>● {selected.priority}</span>
                  </div>
                  <h1 style={{ margin: 0, fontSize: mob ? 16 : 20, fontWeight: 700, color: "#f1f5f9", lineHeight: 1.3, fontFamily: "'IBM Plex Sans', sans-serif" }}>
                    {selected.headline}
                  </h1>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button style={{ fontSize: 11, padding: "6px 14px", background: "#1e3a5f", border: "none", borderRadius: 6, color: "#93c5fd", cursor: "pointer", fontWeight: 600 }}>Edit</button>
                  <button
                    onClick={() => simulateAction("publishAll")}
                    disabled={!!loading.publishAll}
                    style={{ fontSize: 11, padding: "6px 14px", background: "#0d3326", border: "none", borderRadius: 6, color: "#6ee7b7", cursor: loading.publishAll ? "wait" : "pointer", fontWeight: 600, opacity: loading.publishAll ? 0.7 : 1, display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    {loading.publishAll && <span style={{ display: "inline-block", width: 10, height: 10, border: "2px solid #6ee7b740", borderTopColor: "#6ee7b7", borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />}
                    {loading.publishAll ? "Publishing…" : "Publish All"}
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #1e2d42", marginTop: 4, overflowX: mob ? "auto" : "visible", WebkitOverflowScrolling: "touch" }}>
                {[["overview", "Overview"], ["artefacts", "Artefacts"], ["intelligence", "Intelligence"], ["commission", "Commission"], ["seo", mob ? "SEO" : "SEO & Discovery"]].map(([val, label]) => (
                  <button key={val} onClick={() => setActiveTab(val)} style={{
                    fontSize: mob ? 11 : 12, fontWeight: 600, padding: mob ? "8px 10px" : "8px 16px", background: "transparent", border: "none", whiteSpace: "nowrap",
                    borderBottom: `2px solid ${activeTab === val ? "#3b82f6" : "transparent"}`,
                    color: activeTab === val ? "#93c5fd" : "#475569", cursor: "pointer", transition: "all 0.15s",
                    marginBottom: -1,
                  }}>{label}</button>
                ))}
              </div>
            </div>

            {/* Tab content */}
            <div style={{ flex: 1, overflow: "auto", padding: mob ? "14px 14px" : "20px 24px" }}>

              {activeTab === "overview" && (
                <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 280px", gap: mob ? 14 : 20 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {/* Summary */}
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Story Summary</div>
                      <p style={{ margin: 0, fontSize: 13, color: "#94a3b8", lineHeight: 1.7 }}>
                        Flooding on the West Coast has displaced residents and prompted emergency response. Civil Defence activated. Key questions remain around displacement numbers, infrastructure accountability, and insurance exposure. Competitor reporting suggests higher figures than currently confirmed.
                      </p>
                      <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
                        <div><div style={{ fontSize: 10, color: "#475569", marginBottom: 2 }}>Sources</div><div style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0" }}>{selected.sources}</div></div>
                        <div><div style={{ fontSize: 10, color: "#475569", marginBottom: 2 }}>Artefacts</div><div style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0" }}>{selected.artefacts.length}</div></div>
                        <div><div style={{ fontSize: 10, color: "#475569", marginBottom: 2 }}>Published</div><div style={{ fontSize: 16, fontWeight: 700, color: "#10b981" }}>{selected.published.length}</div></div>
                        <div><div style={{ fontSize: 10, color: "#475569", marginBottom: 2 }}>Beat</div><div style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{selected.beat}</div></div>
                      </div>
                    </div>

                    {/* Upload zone */}
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Add to Story</div>
                      <UploadZone />
                    </div>

                    {/* Timeline */}
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Story Timeline</div>
                      {TIMELINE.map((item, i) => <TimelineItem key={i} item={item} />)}
                    </div>
                  </div>

                  {/* Right col */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {/* Sources */}
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Sources</div>
                      {["Civil Defence statement", "Field reporter voice note", "Council PDF", "Satellite imagery (Blair E.)", "Wire — AAP"].slice(0, selected.sources).map((s, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #1e2d42" }}>
                          <span style={{ fontSize: 11 }}>📎</span>
                          <span style={{ fontSize: 11, color: "#94a3b8" }}>{s}</span>
                        </div>
                      ))}
                    </div>

                    {/* Quick artefact status */}
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Output Status</div>
                      {allArtefactTypes.map(type => {
                        const available = selected.artefacts.includes(type);
                        const published = selected.published.includes(type);
                        return (
                          <div key={type} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #1e2d42" }}>
                            <span style={{ fontSize: 14 }}>{ARTEFACT_META[type].icon}</span>
                            <span style={{ fontSize: 11, color: available ? "#e2e8f0" : "#334155", flex: 1 }}>{ARTEFACT_META[type].label}</span>
                            <span style={{ fontSize: 9, fontWeight: 700, color: published ? "#10b981" : available ? "#f59e0b" : "#334155" }}>
                              {published ? "LIVE" : available ? "DRAFT" : "—"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "artefacts" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: mob ? "repeat(2, 1fr)" : "repeat(5, 1fr)", gap: 12 }}>
                    {allArtefactTypes.map(type => (
                      <ArtefactCard key={type} type={type}
                        isPublished={selected.published.includes(type)}
                        isAvailable={selected.artefacts.includes(type)} />
                    ))}
                  </div>
                  {selected.artefacts.includes("article") && (
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 20 }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
                        <span style={{ fontSize: 16 }}>📄</span>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>Article Preview</div>
                        <span style={{ marginLeft: "auto", fontSize: 10, padding: "3px 8px", background: "#0d3326", borderRadius: 4, color: "#10b981", fontWeight: 700 }}>PUBLISHED</span>
                      </div>
                      <h2 style={{ margin: "0 0 8px", fontSize: 18, fontFamily: "'IBM Plex Sans', sans-serif", color: "#f1f5f9" }}>West Coast floods: 'We had minutes to get out,' says resident</h2>
                      <div style={{ fontSize: 11, color: "#475569", marginBottom: 12 }}>By Staff · 10:22am · National</div>
                      <div style={{ background: "#162030", borderRadius: 6, height: 120, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12, border: "1px solid #1e3a5f" }}>
                        <span style={{ fontSize: 11, color: "#334155" }}>🖼️ Lead image — SH6 road damage (BE)</span>
                      </div>
                      <p style={{ margin: 0, fontSize: 13, color: "#94a3b8", lineHeight: 1.7 }}>
                        Residents along the West Coast were evacuated overnight as rivers broke their banks, with emergency services confirming multiple families displaced. Civil Defence has been activated...
                      </p>
                    </div>
                  )}
                  {selected.artefacts.includes("tv-package") && (
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 20 }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
                        <span style={{ fontSize: 16 }}>📺</span>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>TV Package Script</div>
                        <span style={{ marginLeft: "auto", fontSize: 10, padding: "3px 8px", background: "#1e2d42", borderRadius: 4, color: "#f59e0b", fontWeight: 700 }}>DRAFT</span>
                      </div>
                      {[
                        { label: "ANCHOR INTRO", text: "Flooding on the West Coast has forced families from their homes overnight. Our reporter Sarah Morton has the latest." },
                        { label: "REPORTER VO — over B-roll flooding SH6", text: "By dawn, rivers had broken their banks in three locations between Greymouth and Hokitika. Civil Defence confirmed multiple families were moved to emergency accommodation..." },
                        { label: "GRAB — Civil Defence spokesperson", text: "\"We're asking people in low-lying areas to remain vigilant. This isn't over yet.\"" },
                        { label: "REPORTER VO — over B-roll Red Cross site", text: "The Red Cross confirmed a welfare centre was operating in Greymouth. Insurance industry sources say claims could be significant..." },
                        { label: "ANCHOR OUTRO", text: "The Defence Force has been placed on standby. We'll have a full update at six." },
                      ].map((block, i) => (
                        <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #1e2d42" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4, fontFamily: "'IBM Plex Mono', monospace" }}>{block.label}</div>
                          <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.6 }}>{block.text}</div>
                        </div>
                      ))}
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={{ fontSize: 11, padding: "7px 16px", background: "#1e3a5f", border: "none", borderRadius: 6, color: "#93c5fd", cursor: "pointer", fontWeight: 600 }}>Edit Script</button>
                        <button
                          onClick={() => simulateAction("publishAPI")}
                          disabled={!!loading.publishAPI}
                          style={{ fontSize: 11, padding: "7px 16px", background: "#0d3326", border: "none", borderRadius: 6, color: "#6ee7b7", cursor: loading.publishAPI ? "wait" : "pointer", fontWeight: 600, opacity: loading.publishAPI ? 0.7 : 1, display: "inline-flex", alignItems: "center", gap: 6 }}
                        >
                          {loading.publishAPI && <span style={{ display: "inline-block", width: 10, height: 10, border: "2px solid #6ee7b740", borderTopColor: "#6ee7b7", borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />}
                          {loading.publishAPI ? "Publishing…" : "Publish to API"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "intelligence" && (
                <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1fr", gap: mob ? 12 : 16 }}>
                  <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Competitor Coverage</div>
                    {RELATED.map((r, i) => (
                      <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid #1e2d42" }}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                          <span style={{ fontSize: 9, fontWeight: 700, color: "#f59e0b", background: "#3d280020", padding: "1px 6px", borderRadius: 3 }}>{r.pub}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "#e2e8f0", marginBottom: 4, fontFamily: "'IBM Plex Sans', sans-serif" }}>{r.headline}</div>
                        <div style={{ fontSize: 11, color: "#ef4444" }}>⚠ {r.delta}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>AI Suggestions</div>
                      {SUGGESTIONS.map((s, i) => (
                        <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid #1e2d42", alignItems: "flex-start" }}>
                          <span style={{ fontSize: 11, color: "#10b981", marginTop: 1 }}>→</span>
                          <span style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.5 }}>{s}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Related Story Ideas</div>
                      {["Infrastructure underinvestment: a pattern across the Coast", "Climate and flood risk: what the data says", "How NZ's insurance industry is pricing weather events"].map((s, i) => (
                        <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid #1e2d42", alignItems: "center" }}>
                          <span style={{ fontSize: 12, color: "#94a3b8", flex: 1, fontFamily: "'IBM Plex Sans', sans-serif" }}>{s}</span>
                          <button style={{ fontSize: 10, padding: "3px 8px", background: "#1e3a5f", border: "none", borderRadius: 4, color: "#93c5fd", cursor: "pointer", fontWeight: 600, flexShrink: 0 }}>Create Slug</button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "commission" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Active Commissions</div>
                    {COMMISSIONS.map((c, i) => (
                      <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 0", borderBottom: "1px solid #1e2d42" }}>
                        <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#1e3a5f", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#93c5fd", flexShrink: 0 }}>
                          {c.reporter.split(" ").map(w => w[0]).join("")}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: "#e2e8f0", marginBottom: 2 }}>{c.task}</div>
                          <div style={{ fontSize: 11, color: "#475569" }}>{c.reporter} · Due {c.due}</div>
                        </div>
                        <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", padding: "2px 8px", borderRadius: 20,
                          color: c.status === "accepted" ? "#10b981" : "#f59e0b",
                          background: c.status === "accepted" ? "#0d3326" : "#3d2800",
                        }}>{c.status}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>New Commission</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <input placeholder="What do you need?" style={{ background: "#162030", border: "1px solid #1e3a5f", borderRadius: 6, padding: "9px 12px", color: "#e2e8f0", fontSize: 12, outline: "none" }} />
                      <div style={{ display: "flex", flexDirection: mob ? "column" : "row", gap: 10 }}>
                        <input placeholder="Assign to reporter..." style={{ flex: 1, background: "#162030", border: "1px solid #1e3a5f", borderRadius: 6, padding: "9px 12px", color: "#e2e8f0", fontSize: 12, outline: "none" }} />
                        <input placeholder="Due..." style={{ width: mob ? "100%" : 120, background: "#162030", border: "1px solid #1e3a5f", borderRadius: 6, padding: "9px 12px", color: "#e2e8f0", fontSize: 12, outline: "none" }} />
                      </div>
                      <button
                        onClick={() => simulateAction("sendCommission")}
                        disabled={!!loading.sendCommission}
                        style={{ padding: "9px 20px", background: "#1e3a5f", border: "none", borderRadius: 6, color: "#93c5fd", cursor: loading.sendCommission ? "wait" : "pointer", fontSize: 12, fontWeight: 700, alignSelf: "flex-start", opacity: loading.sendCommission ? 0.7 : 1, display: "inline-flex", alignItems: "center", gap: 6 }}
                      >
                        {loading.sendCommission && <span style={{ display: "inline-block", width: 10, height: 10, border: "2px solid #93c5fd40", borderTopColor: "#93c5fd", borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />}
                        {loading.sendCommission ? "Sending…" : "Send Commission"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "seo" && (
                <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1fr", gap: mob ? 12 : 16 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Suggested Headlines</div>
                      {HEADLINES.map((h, i) => (
                        <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid #1e2d42", alignItems: "center" }}>
                          <span style={{ fontSize: 12, color: "#94a3b8", flex: 1, fontFamily: "'IBM Plex Sans', sans-serif", lineHeight: 1.4 }}>{h}</span>
                          <button style={{ fontSize: 10, padding: "3px 8px", background: "#1e3a5f", border: "none", borderRadius: 4, color: "#93c5fd", cursor: "pointer", fontWeight: 600, flexShrink: 0 }}>Use</button>
                        </div>
                      ))}
                    </div>
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>SEO Metadata</div>
                      <div style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}>Meta title</div>
                      <div style={{ fontSize: 12, color: "#94a3b8", background: "#162030", borderRadius: 4, padding: "8px 10px", marginBottom: 10 }}>West Coast Floods 2026: Families displaced, Civil Defence activated</div>
                      <div style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}>Meta description</div>
                      <div style={{ fontSize: 12, color: "#94a3b8", background: "#162030", borderRadius: 4, padding: "8px 10px", marginBottom: 10, lineHeight: 1.5 }}>Flooding has displaced families on the West Coast overnight. Here's what we know about the emergency response, road closures, and what comes next.</div>
                      <div style={{ fontSize: 11, color: "#475569", marginBottom: 6 }}>Keywords</div>
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {["West Coast flooding", "New Zealand flood", "Civil Defence", "Greymouth flood 2026", "SH6 closure", "flood evacuation NZ"].map(k => (
                          <span key={k} style={{ fontSize: 10, padding: "3px 8px", background: "#1e3a5f", borderRadius: 4, color: "#93c5fd" }}>{k}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Social Images</div>
                      {[["Facebook / Open Graph", "1200 × 630px"], ["Twitter / X Card", "1200 × 675px"], ["Vertical / Instagram", "1080 × 1350px"]].map(([label, size]) => (
                        <div key={label} style={{ padding: "8px 0", borderBottom: "1px solid #1e2d42", display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 40, height: 24, background: "#162030", borderRadius: 3, border: "1px solid #1e3a5f", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span style={{ fontSize: 9 }}>🖼️</span>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: "#e2e8f0" }}>{label}</div>
                            <div style={{ fontSize: 10, color: "#475569" }}>{size}</div>
                          </div>
                          <button style={{ fontSize: 10, padding: "3px 8px", background: "#1e3a5f", border: "none", borderRadius: 4, color: "#93c5fd", cursor: "pointer", fontWeight: 600 }}>Generate</button>
                        </div>
                      ))}
                    </div>
                    <div style={{ background: "#0f1623", border: "1px solid #1e2d42", borderRadius: 8, padding: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Social Copy</div>
                      {[["Facebook", "Families have been forced from their homes overnight as flooding hit the West Coast. Civil Defence is activated — here's what we know."], ["X / Twitter", "BREAKING: West Coast flooding forces evacuations. Civil Defence activated. Road closures on SH6. Full coverage →"], ["Instagram Caption", "📍 West Coast, New Zealand. Rivers broke their banks overnight. Families evacuated. Our team is on the ground."]].map(([platform, copy]) => (
                        <div key={platform} style={{ padding: "8px 0", borderBottom: "1px solid #1e2d42" }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#3b82f6", marginBottom: 4 }}>{platform}</div>
                          <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.5 }}>{copy}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
